import { extractedKind, hostOf, type Attachment } from '@pa/core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mediaUrl } from '../lib/api';
import { formatBytes } from '../lib/image';
import { formatDuration } from '../lib/recorder';
import { ImageViewer } from './ImageViewer';
import { FileIcon, LinkIcon, Pause, Play } from './ui/icons';

/**
 * Attachment bubbles for a message: a voice note with play/pause, duration and a collapsible
 * transcript; an image thumbnail that opens the in-app viewer; a file bubble with name, size
 * and page count; a link card (domain and title, no favicon). User attachments follow the
 * plain-on-canvas rule with a small surface control.
 */
export function AttachmentList({
  attachments,
  localUrls,
  align,
}: {
  attachments: readonly Attachment[];
  localUrls?: Record<string, string> | undefined;
  align: 'start' | 'end';
}) {
  if (attachments.length === 0) return null;
  return (
    <ul
      className={[
        'flex max-w-[85%] flex-col gap-2',
        align === 'end' ? 'items-end' : 'items-start',
      ].join(' ')}
    >
      {attachments.map((a) => (
        <li key={a.attachment_id} className="max-w-full">
          {a.kind === 'audio' ? (
            <AudioBubble
              attachment={a}
              src={localUrls?.[a.attachment_id] ?? mediaUrl(a.attachment_id)}
            />
          ) : a.kind === 'image' ? (
            <ImageBubble
              attachment={a}
              src={localUrls?.[a.attachment_id] ?? mediaUrl(a.attachment_id)}
            />
          ) : a.kind === 'document' ? (
            <FileBubble attachment={a} />
          ) : (
            <LinkCard attachment={a} />
          )}
        </li>
      ))}
    </ul>
  );
}

function AudioBubble({ attachment, src }: { attachment: Attachment; src: string }) {
  const { t } = useTranslation();
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [open, setOpen] = useState(false);
  const duration = attachment.duration_ms ?? 0;

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setPosition(el.currentTime * 1000);
    const onEnd = () => {
      setPlaying(false);
      setPosition(0);
    };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('ended', onEnd);
    el.addEventListener('pause', () => setPlaying(false));
    el.addEventListener('play', () => setPlaying(true));
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('ended', onEnd);
    };
  }, []);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => setPlaying(false));
    else el.pause();
  };
  const fraction = duration ? Math.min(1, position / duration) : 0;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex min-w-56 items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2 shadow-sm dark:bg-elevated">
        <audio ref={audioRef} src={src} preload="metadata" />
        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? t('two.audio.pause') : t('two.audio.play')}
          className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand"
        >
          {playing ? <Pause size={18} /> : <Play size={18} />}
        </button>
        <div className="flex flex-1 flex-col gap-1" dir="ltr">
          <div className="h-1 overflow-hidden rounded-full bg-line" aria-hidden>
            <div
              className="h-full bg-brand transition-[width]"
              style={{ width: `${fraction * 100}%` }}
            />
          </div>
          <span className="tabular text-xs text-ink-3">
            {formatDuration(playing || position ? position : duration)}
            {duration ? ` / ${formatDuration(duration)}` : ''}
          </span>
        </div>
      </div>
      {attachment.transcript ? (
        <div className="text-sm">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="min-h-9 text-xs font-medium text-ink-2 hover:text-ink"
          >
            {open ? t('two.audio.hideTranscript') : t('two.audio.showTranscript')}
          </button>
          {open ? (
            <p className="whitespace-pre-wrap text-ink-2" dir="auto" lang="">
              {attachment.transcript}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ImageBubble({ attachment, src }: { attachment: Attachment; src: string }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const thumbRef = useRef<HTMLButtonElement>(null);
  const kind = extractedKind(attachment);
  return (
    <>
      <button
        ref={thumbRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('two.image.open')}
        className="block overflow-hidden rounded-lg border border-line bg-surface shadow-sm"
      >
        <img
          src={src}
          alt={attachment.filename ?? ''}
          loading="lazy"
          width={attachment.width}
          height={attachment.height}
          className="block max-h-60 max-w-60 object-cover"
        />
      </button>
      {kind ? (
        <p className="mt-1 text-xs text-ink-3">
          {t(`two.image.kind.${kind}`, { defaultValue: kind })}
        </p>
      ) : null}
      {open ? (
        <ImageViewer
          src={src}
          alt={attachment.filename ?? ''}
          onClose={() => setOpen(false)}
          returnFocusTo={thumbRef}
        />
      ) : null}
    </>
  );
}

function FileBubble({ attachment }: { attachment: Attachment }) {
  const { t, i18n } = useTranslation();
  const meta = [
    formatBytes(attachment.size_bytes, i18n.resolvedLanguage ?? 'en'),
    attachment.pages ? t('two.file.pages', { count: attachment.pages }) : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <a
      href={mediaUrl(attachment.attachment_id)}
      target="_blank"
      rel="noreferrer"
      className="flex min-w-56 items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2 shadow-sm hover:bg-canvas dark:bg-elevated"
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-brand-soft text-brand">
        <FileIcon size={20} />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium text-ink" dir="auto">
          {attachment.filename ?? t('two.file.document')}
        </span>
        <span className="text-xs text-ink-3">{meta}</span>
      </span>
    </a>
  );
}

function LinkCard({ attachment }: { attachment: Attachment }) {
  const { t } = useTranslation();
  const url = attachment.url ?? '';
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="flex max-w-72 items-center gap-3 rounded-lg border border-line bg-surface px-3 py-2 shadow-sm hover:bg-canvas dark:bg-elevated"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-canvas text-ink-2">
        <LinkIcon size={18} />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="truncate text-xs text-ink-3" dir="ltr">
          {hostOf(url)}
        </span>
        <span className="line-clamp-2 text-sm text-ink" dir="auto">
          {attachment.title || t('two.link.untitled')}
        </span>
      </span>
    </a>
  );
}
